import { inject, Injectable } from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { isUndefined } from '@sama/micro-dash';
import { flatten } from 'lodash';
import { combineLatest, Observable, of } from 'rxjs';
import { filter as filterOperator, map, switchMap } from 'rxjs/operators';
import { ObjectState } from '../../../cross-team-shared/annotation/shared/state/object-state';
import { ShapeState } from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { keys } from '../../../cross-team-shared/misc-utils/keys';
import {
  getOrThrow,
  strictFilter,
} from '../../../cross-team-shared/misc-utils/strict-record';
import { EventTrackingService } from '../../../shared/event-tracking/event-tracking.service';
import { getShapesRecordFromShapeInfo } from '../../h3ml/state/task-workspace-state.utils';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { RootUndoService } from './root-undo.service';

@Injectable()
export class ObjectScopingService {
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private eventTrackingService = inject(EventTrackingService);
  private undoService = inject(RootUndoService);

  getObjectInScopeContainingShape(shape: ShapeState) {
    const parentId = shape.parentId;
    assert(isDefined(parentId), `Shape ${shape.index} has no parent`);
    const { scopedObjectId } = this.taskWorkspaceStore.state();
    if (parentId === scopedObjectId) {
      return undefined;
    }
    return this.getObjectInScopeContainingNestedObject(parentId).index;
  }

  getNestedShapes(objectId: number) {
    const { outputAnnotationInfo } = this.taskWorkspaceStore.state();
    const allShapes = getShapesRecordFromShapeInfo(outputAnnotationInfo);

    const object = this.taskWorkspaceStore.getObjectStore(objectId).state();
    const objects = [object, ...this.getNestedObjects(objectId)];

    return objects.flatMap((obj) =>
      obj.childShapeIds.map((shapeId) => getOrThrow(allShapes, shapeId)),
    );
  }

  getNestedObjectIds$(objectId: number): Observable<number[]> {
    return this.taskWorkspaceStore
      .getObjectStore(objectId)('childObjectIds')
      .$.pipe(
        // unfortunately, deleting an object can emit this first before it is
        // unsubscribed
        filterOperator(isDefined),
        switchMap((objectIds) =>
          combineLatest([
            ...objectIds.map((nestedObjectId) =>
              this.getNestedObjectIds$(nestedObjectId),
            ),
            of(objectIds),
          ]),
        ),
        map(flatten),
      );
  }

  getRootParentObjectId(objectId: number): undefined | number {
    const parentId = this.getParentObjectId(objectId);
    if (isUndefined(parentId)) {
      return objectId;
    }
    return this.getRootParentObjectId(parentId);
  }

  getParentObjectId(objectId = this.taskWorkspaceStore.state().scopedObjectId) {
    const { objects } = this.taskWorkspaceStore.state();
    if (isUndefined(objectId)) {
      return undefined;
    }

    const object = objects[objectId];
    assert(isDefined(object), 'scoped object should exist in objects');

    return object.parentId;
  }

  getNestedObjects(objectId: number): ObjectState[] {
    const { objects } = this.taskWorkspaceStore.state();
    const object = this.taskWorkspaceStore.getObjectStore(objectId).state();

    const nestedObjects = object.childObjectIds
      .map((childObjectId) => objects[childObjectId])
      .filter(isDefined);

    return [
      ...nestedObjects,
      ...nestedObjects.flatMap((nestedObject) =>
        this.getNestedObjects(nestedObject.index),
      ),
    ];
  }

  getAllObjectIdsInScope$() {
    return combineLatest([
      this.taskWorkspaceStore('scopedObjectId').$,
      this.taskWorkspaceStore('objects').$,
    ]).pipe(map(() => this.getAllObjectIdsInScope()));
  }

  getObjectsInSpecifiedScope(scopedObjectId?: number) {
    return strictFilter(
      this.taskWorkspaceStore.state().objects,
      (objectItem) => objectItem.parentId === scopedObjectId,
    );
  }

  scopeObject(
    objectId: number | undefined,
    source: string,
    method: 'unscope' | 'scope' = 'unscope',
  ) {
    if (this.taskWorkspaceStore.state().scopedObjectId === objectId) {
      return;
    }
    this.undoService.currentUndoState.scopedObjectId = objectId;
    this.taskWorkspaceStore('scopedObjectId').set(objectId);
    this.eventTrackingService.track({
      id: 'scope_object',
      bucket: 'navigation',
      sub_bucket: 'frame',
      objectId,
      source,
      method,
    });
  }

  scopeIntoView(objectId: number) {
    const { objects } = this.taskWorkspaceStore.state();
    const object = getOrThrow(objects, objectId);
    this.scopeObject(object.parentId, 'mouse_single', 'scope');
  }

  scopeObjectContainingShape(shape: ShapeState) {
    const objectIndex = this.getObjectInScopeContainingShape(shape);
    if (isDefined(objectIndex)) {
      this.scopeObject(objectIndex, 'shape_double_click', 'scope');
    }
  }

  private getObjectInScopeContainingNestedObject(
    objectId: number,
  ): ObjectState {
    const { objects, scopedObjectId } = this.taskWorkspaceStore.state();
    const object = getOrThrow(objects, objectId);
    if (object.parentId === scopedObjectId || isUndefined(object.parentId)) {
      return object;
    }
    return this.getObjectInScopeContainingNestedObject(object.parentId);
  }

  private getAllObjectIdsInScope() {
    const { scopedObjectId, objects } = this.taskWorkspaceStore.state();

    if (isUndefined(scopedObjectId)) {
      return keys(objects);
    }

    const nestedObjects = this.getNestedObjects(scopedObjectId);
    return nestedObjects.map((object) => object.index);
  }
}
